/**
 * Shared default data structures for printer configuration.
 * Used by usePrinterConfig and usePrepheralCardFooter to avoid duplication.
 */

// Printer ticket types (the `tickets.value` carried by each printer row).
export const TICKET_VALUE_CUSTOMER = "01";
export const TICKET_VALUE_PRODUCTION = "02";
export const TICKET_VALUE_STICKER = "03";
export const TICKET_VALUE_VOUCHER = "04";
// Legacy rows may carry the customer ticket value as the number 1.
const TICKET_VALUE_CUSTOMER_LEGACY = 1;

/**
 * True when the ticket is a customer receipt (value "01", or legacy numeric 1).
 * Single source of truth shared by the Peripherals add flow and the
 * associate-modal flow so both compute the same default configuration.
 */
export function isCustomerTicket(value: unknown): boolean {
    return (
        value === TICKET_VALUE_CUSTOMER ||
        value === TICKET_VALUE_CUSTOMER_LEGACY
    );
}

/**
 * True when the ticket is printed in the kitchen (Production "02",
 * Sticker "03", or Voucher "04"). Sticker is intentionally included so a
 * Sticker printer gets the same Kitchen_ticket defaults wherever it is added.
 */
export function isKitchenTicket(value: unknown): boolean {
    return (
        value === TICKET_VALUE_PRODUCTION ||
        value === TICKET_VALUE_STICKER ||
        value === TICKET_VALUE_VOUCHER
    );
}

export function mapSalesModes(
    salesModes: any[],
    state: boolean
): { designation: string; state: boolean }[] {
    return salesModes.map((el: any) => ({
        designation: el.designation,
        state,
    }));
}

export function createDefaultKitchenTicket(
    salesModes: any[],
    isKitchenActive: boolean
): any {
    return {
        note_kitchen_ticket: {
            print_note: isKitchenActive,
        },
        ticketKitchenModeSale: mapSalesModes(salesModes, isKitchenActive),
        customerAddressModeSale: mapSalesModes(salesModes, false),
        customer_information_mode_sale: {
            customerInformationModeSaleName: mapSalesModes(salesModes, false),
            customerInformationModeSaleNumTel: mapSalesModes(salesModes, false),
            customerInformationModeSaleCity: mapSalesModes(salesModes, false),
            customerInformationModeSaleAdress: mapSalesModes(salesModes, false),
            customerInformationModeSaleCode: mapSalesModes(salesModes, false),
            customerInformationModeSaleCompany: mapSalesModes(salesModes, false),
        },
    };
}

export function createDefaultCustomerTicket(
    salesModes: any[],
    isCustomerActive: boolean
): any {
    return {
        customerTicketSalesModes: mapSalesModes(salesModes, isCustomerActive),
        dateTimeDeliveryModeSale: mapSalesModes(salesModes, false),
        print_date_time_delivery_mode_sale: {
            provider_name: false,
            without_price: isCustomerActive,
            print_notes: isCustomerActive,
            items_lines: false,
            method_payment: false,
            method_payment_TVA: false,
        },
    };
}

export function createDefaultModelKitchenTicket(): any {
    return {
        model_1: {
            black_background: true,
            isChecked: true,
            smaller_font: true,
        },
        model_2: {
            isChecked: false,
            menu_component: false,
            name_menu: false,
            online_order: false,
            single_label: false,
        },
        model_3: {
            Activate_manual_dispatch_follow: false,
            dispatch_automatically_follow: false,
            follow_kitchen_ticket: false,
            font_small: false,
            isChecked: false,
            order_kitchen: false,
            Send_changes_kitchen: false,
            ticket_additions_cancellations: false,
        },
        model_4: { isChecked: false },
        model_5: { isChecked: false },
    };
}

export function createDefaultGeneralSettings(
    includeOrderNumber: boolean = false
): any {
    const ticketHeader: any = {
        logo_customer: false,
        logo_kitchen: false,
        Path_customer: "",
        Path_kitchen: "",
        sale_mode_bold: true,
        name_phone_number_bold: false,
        sale_mode_black_backround: false,
    };
    if (includeOrderNumber) {
        ticketHeader.ticketHeaderStyleOrderNumber = false;
    }
    return {
        automatic_printing: {
            NumberCustomer: 1,
            NumberTicketsKitchen: 1,
        },
        information_mode: {
            information_bold: false,
            printer_information_mode: false,
        },
        ticket_header: ticketHeader,
    };
}

// Default barcode/QR position when none is chosen yet ("2" = bottom).
const DEFAULT_CODE_POSITION = "2";

function createDefaultCodeSlot(): any {
    return {
        barcodeChecked: false,
        barcodePosition: DEFAULT_CODE_POSITION,
        qrCodeChecked: false,
        qrCodePosition: DEFAULT_CODE_POSITION,
        qr_code_config: { type: "", url: "", image: "" },
    };
}

export function createDefaultDiversSettings(): any {
    // Uses the schema the Divers editor actually reads/writes (Code.pending /
    // Code.order with qr_code_config + alone_printing). The legacy flat Code
    // shape (barcode_ticket / checked_barcode_ticket / QR_code) is dead — no
    // tab reads it — so a printer created with it showed blank toggles and got
    // silently overwritten on first save.
    return {
        alone_printing: { station_prints: false },
        printing_options: {
            force_paper: false,
            special_characters: false,
        },
        Code: {
            pending: createDefaultCodeSlot(),
            order: createDefaultCodeSlot(),
        },
        paperless_ticket: {
            paperless_ticket_custom: true,
        },
    };
}
